<template>
  <Tooltip
    v-if="latest"
    placement="top"
    :width-class="'w-max'"
  >
    <template #trigger>
      <button
        type="button"
        :class="[
          'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap transition-opacity',
          theme.pillClass,
          clickable ? 'cursor-pointer hover:opacity-90' : 'cursor-default',
        ]"
        :tabindex="clickable ? 0 : -1"
        :aria-label="theme.label"
        @click.stop="onClick"
      >
        <span>{{ theme.label }}</span>
        <span
          v-if="extraCount > 0"
          class="ml-1 inline-flex items-center justify-center rounded-full bg-white/70 px-1.5 py-px text-[10px] font-semibold leading-none"
          :class="theme.headingClass"
          :aria-label="`${extraCount} additional complaints`"
        >+{{ extraCount }}</span>
      </button>
    </template>

    <div class="text-xs whitespace-nowrap text-left flex flex-col gap-0.5 px-1 py-0.5">
      <span v-if="ticketDisplay" class="font-semibold">{{ ticketDisplay }}</span>
      <span v-if="createdAtDisplay" class="text-gray-500">{{ createdAtDisplay }}</span>
      <span v-if="extraCount > 0" class="text-gray-500">
        +{{ extraCount }} more {{ extraCount === 1 ? 'complaint' : 'complaints' }}
      </span>
    </div>
  </Tooltip>
</template>

<script setup lang="ts">
import Tooltip from '@/components/Shared/Tooltip.vue';
import {
  formatComplaintTicketNo,
  getComplaintStatusTheme,
  getExtraComplaintCount,
  getLatestComplaint,
} from '@/composables';
import { formatTimestampWithBrowserTimezone } from '@/composables/useTimeZoneFormatDate';
import { Complaint } from '@/models';
import { computed } from 'vue';

interface Props {
  complaints: Complaint[];
  clickable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  clickable: false,
});

const emit = defineEmits<{
  pillClick: [complaint: Complaint];
}>();

const latest = computed<Complaint | null>(() =>
  getLatestComplaint({ complaints: props.complaints })
);

const theme = computed(() => getComplaintStatusTheme(latest.value?.status));

const extraCount = computed(() =>
  getExtraComplaintCount({ complaints: props.complaints })
);

const ticketDisplay = computed(() =>
  latest.value ? formatComplaintTicketNo(latest.value.ticket_no) : null
);

const createdAtDisplay = computed(() => {
  if (!latest.value?.created_at) return null;
  return formatTimestampWithBrowserTimezone(latest.value.created_at);
});

const onClick = () => {
  if (props.clickable && latest.value) {
    emit('pillClick', latest.value);
  }
};
</script>
